// RUTA: src/contexts/RestTimerContext.tsx
//
// Estado GLOBAL del cronómetro de descanso. Antes el estado vivía
// dentro del componente <RestTimer/>, así que cuando el cliente
// cerraba el modal o navegaba a otra parte de la rutina, el timer
// moría con el componente.
//
// Ahora el timer vive aquí (en el árbol del App, encima del Router),
// la UI (modal expandido + pill flotante) son solo "vistas" del
// estado. El cliente puede:
//   1. Iniciar timer en un ejercicio
//   2. Minimizar el modal → aparece el pill flotante arriba
//   3. Navegar a otros ejercicios o secciones — timer sigue corriendo
//   4. Tap en el pill para volver al modal full
//
// Patrón usado por Hevy, Strong, Trainerize.
//
// Audio engine, wake lock, vibración y notificaciones también viven
// aquí porque están atados al ciclo del timer, no a la UI.

import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react';

// ───────── Audio engine compartido (igual que el original) ─────────

let sharedAudioCtx: AudioContext | null = null;
let silentAudio: HTMLAudioElement | null = null;
let audioUnlocked = false;

function getAudioContext(): AudioContext | null {
  try {
    if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
      sharedAudioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (sharedAudioCtx.state === 'suspended') sharedAudioCtx.resume();
    return sharedAudioCtx;
  } catch { return null; }
}

function unlockAudio() {
  if (audioUnlocked) return;
  const ctx = getAudioContext();
  if (ctx) {
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  }
  try {
    silentAudio = new Audio();
    silentAudio.play().catch(() => {});
  } catch { /* ignore */ }
  audioUnlocked = true;
}

/** Beep. Subimos el volumen (0.6 antes → 0.85 ahora) y permitimos
 *  duración más larga para que se escuche aunque el cliente esté
 *  distraído en gym ruidoso. */
function playBeep(frequency = 880, duration = 0.5, volume = 0.85) {
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = frequency;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.02);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  } catch { /* ignore */ }
}

async function requestWakeLock(): Promise<WakeLockSentinel | null> {
  try {
    if ('wakeLock' in navigator) {
      return await (navigator as any).wakeLock.request('screen');
    }
  } catch { /* ignore */ }
  return null;
}

async function requestNotificationPermission() {
  try {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  } catch { /* ignore */ }
}

/** Notificación local cuando termina el timer. Las notificaciones
 *  tienen su propio canal de sonido — funcionan en silent mode si
 *  el cliente las ha permitido. */
function sendTimerEndNotification(exerciseName?: string) {
  try {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    new Notification('⏱️ Descanso terminado', {
      body: exerciseName
        ? `Toca tu siguiente serie de ${exerciseName}`
        : 'Listo para tu siguiente serie',
      icon: '/pwa-icon-192.png',
      badge: '/pwa-icon-192.png',
      tag: 'rest-timer-end',
      requireInteraction: false,
      silent: false,
    });
  } catch { /* ignore */ }
}

// ───────── Helpers ─────────

export function formatTime(s: number) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export function restOptionToSeconds(option: string): number {
  return Math.round(parseFloat(option) * 60);
}

// ───────── Tipos del contexto ─────────

interface TimerSnapshot {
  totalSeconds: number;
  remaining: number;
  running: boolean;
  started: boolean;
  alarming: boolean;
  exerciseName: string | null;
  presets: { label: string; seconds: number }[];
  /** True cuando el modal full-screen está abierto */
  modalOpen: boolean;
  /** True cuando el cliente está en sesión (timer arrancado y no
   *  reseteado/cerrado todavía) */
  active: boolean;
}

interface TimerActions {
  /** Abre el modal expandido. Si no hay sesión, prepara presets. */
  openModal: (params: { exerciseName?: string; restOptions?: string[] }) => void;
  /** Cierra el modal SIN cancelar el timer (se queda corriendo en
   *  background, mostrando el pill flotante). */
  minimize: () => void;
  /** Cancela completamente el timer y cierra el modal. */
  cancel: () => void;
  start: () => void;
  togglePause: () => void;
  reset: () => void;
  selectPreset: (seconds: number) => void;
  adjustSeconds: (delta: number) => void;
  stopAlarm: () => void;
}

const RestTimerContext = createContext<(TimerSnapshot & TimerActions) | null>(null);

const DEFAULT_PRESETS = [
  { label: '1:00', seconds: 60 },
  { label: '1:30', seconds: 90 },
  { label: '2:00', seconds: 120 },
  { label: '2:30', seconds: 150 },
  { label: '3:00', seconds: 180 },
];

// ───────── Provider ─────────

export function RestTimerProvider({ children }: { children: ReactNode }) {
  const [totalSeconds, setTotalSeconds] = useState(90);
  const [remaining, setRemaining] = useState(90);
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const [alarming, setAlarming] = useState(false);
  const [exerciseName, setExerciseName] = useState<string | null>(null);
  const [presets, setPresets] = useState(DEFAULT_PRESETS);
  const [modalOpen, setModalOpen] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const alarmRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const endTimeRef = useRef<number>(0);
  const exerciseNameRef = useRef<string | null>(null);
  exerciseNameRef.current = exerciseName;

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  const stopAlarm = useCallback(() => {
    if (alarmRef.current) {
      clearInterval(alarmRef.current);
      alarmRef.current = null;
    }
    setAlarming(false);
    if ('vibrate' in navigator) navigator.vibrate(0);
  }, []);

  const stopTicking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRunning(false);
  }, []);

  const cancel = useCallback(() => {
    stopTicking();
    stopAlarm();
    releaseWakeLock();
    setStarted(false);
    setRemaining(totalSeconds);
    setModalOpen(false);
    setExerciseName(null);
  }, [releaseWakeLock, stopAlarm, stopTicking, totalSeconds]);

  const reset = useCallback(() => {
    stopTicking();
    stopAlarm();
    releaseWakeLock();
    setStarted(false);
    setRemaining(totalSeconds);
  }, [releaseWakeLock, stopAlarm, stopTicking, totalSeconds]);

  // Loop principal del countdown
  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const secsLeft = Math.max(0, Math.round((endTimeRef.current - now) / 1000));
      setRemaining(prev => {
        if (secsLeft <= 0 && prev > 0) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setRunning(false);
          setAlarming(true);
          // Notificación (suena en silent mode si está permitida)
          sendTimerEndNotification(exerciseNameRef.current || undefined);
          // Beep doble más fuerte y largo
          playBeep(880, 0.6, 0.9);
          setTimeout(() => playBeep(1100, 0.9, 0.9), 500);
          // Vibración fuerte (Android)
          if ('vibrate' in navigator) navigator.vibrate([300, 150, 300, 150, 500]);
          // Alarma repetida cada 1.5s
          alarmRef.current = setInterval(() => {
            playBeep(880, 0.6, 0.9);
            setTimeout(() => playBeep(1100, 0.9, 0.9), 500);
            if ('vibrate' in navigator) navigator.vibrate([300, 150, 300, 150, 500]);
          }, 1500);
          return 0;
        }
        if (secsLeft <= 10 && secsLeft > 0 && secsLeft !== prev) {
          playBeep(660, 0.12, 0.4);
        }
        return secsLeft;
      });
    }, 500);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running]);

  // Cuando el cliente cambia el preset y aún no arrancó, sincroniza
  // remaining con totalSeconds.
  useEffect(() => {
    if (!started) setRemaining(totalSeconds);
  }, [totalSeconds, started]);

  // Re-pedir wake lock al volver a la pestaña
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState === 'visible' && running) {
        const wl = await requestWakeLock();
        if (wl) wakeLockRef.current = wl;
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [running]);

  // Cleanup al desmontar el provider (rara vez ocurre — solo en logout)
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (alarmRef.current) clearInterval(alarmRef.current);
      releaseWakeLock();
    };
  }, [releaseWakeLock]);

  // ───── Acciones ─────

  const openModal = useCallback((params: { exerciseName?: string; restOptions?: string[] }) => {
    const newPresets = (params.restOptions || []).length > 0
      ? params.restOptions!.map(o => ({
          label: formatTime(restOptionToSeconds(o)),
          seconds: restOptionToSeconds(o),
        }))
      : DEFAULT_PRESETS;

    // Si ya hay timer activo (started/running/alarming), NO sobreescribimos
    // los presets ni el ejercicio — solo mostramos el modal con el estado
    // actual. Esto permite que el cliente abra otro ejercicio mientras
    // descansa y al toquear "rest timer" vuelva a ver SU timer activo.
    const isActive = started || running || alarming;
    if (!isActive) {
      setPresets(newPresets);
      setTotalSeconds(newPresets[0]?.seconds ?? 90);
      setRemaining(newPresets[0]?.seconds ?? 90);
      setStarted(false);
      setRunning(false);
      setExerciseName(params.exerciseName ?? null);
    }
    setModalOpen(true);
    requestNotificationPermission();
  }, [started, running, alarming]);

  const minimize = useCallback(() => {
    setModalOpen(false);
  }, []);

  const start = useCallback(async () => {
    unlockAudio();
    setStarted(true);
    setRunning(true);
    endTimeRef.current = Date.now() + totalSeconds * 1000;
    const wl = await requestWakeLock();
    if (wl) wakeLockRef.current = wl;
  }, [totalSeconds]);

  const togglePause = useCallback(async () => {
    if (running) {
      stopTicking();
    } else {
      unlockAudio();
      endTimeRef.current = Date.now() + remaining * 1000;
      setRunning(true);
      const wl = await requestWakeLock();
      if (wl) wakeLockRef.current = wl;
    }
  }, [running, remaining, stopTicking]);

  const selectPreset = useCallback((seconds: number) => {
    stopTicking();
    stopAlarm();
    releaseWakeLock();
    setStarted(false);
    setTotalSeconds(seconds);
    setRemaining(seconds);
  }, [releaseWakeLock, stopAlarm, stopTicking]);

  const adjustSeconds = useCallback((delta: number) => {
    if (started) return;
    setTotalSeconds(prev => Math.max(10, Math.min(600, prev + delta)));
  }, [started]);

  const active = started || running || alarming;

  const value: TimerSnapshot & TimerActions = {
    totalSeconds, remaining, running, started, alarming,
    exerciseName, presets, modalOpen, active,
    openModal, minimize, cancel,
    start, togglePause, reset, selectPreset, adjustSeconds, stopAlarm,
  };

  return (
    <RestTimerContext.Provider value={value}>
      {children}
    </RestTimerContext.Provider>
  );
}

export function useRestTimer() {
  const ctx = useContext(RestTimerContext);
  if (!ctx) throw new Error('useRestTimer must be used inside RestTimerProvider');
  return ctx;
}
